/**
 * Mounts the cookie consent (ADR-0048) without costing the first paint: the banner's code is fetched only when it is
 * needed — no stored choice (after the page has painted and the browser is idle) or "Cookie settings" was pressed.
 */
import { lazy, Suspense, useEffect, useState } from "react";
import { onConsentSettingsOpen, useConsent } from "../lib/consent";

const CookieConsent = lazy(() => import("./CookieConsent"));

function whenIdle(fn: () => void): () => void {
  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(fn, { timeout: 2500 });
    return () => window.cancelIdleCallback(id);
  }
  const id = setTimeout(fn, 1200);
  return () => clearTimeout(id);
}

export function ConsentHost() {
  const consent = useConsent();
  const [ready, setReady] = useState(false);
  const [settings, setSettings] = useState(false);

  useEffect(
    () =>
      onConsentSettingsOpen(() => {
        setSettings(true);
        setReady(true);
      }),
    [],
  );
  useEffect(() => {
    if (consent || ready) return;
    let cancel = () => {};
    const start = () => {
      cancel = whenIdle(() => setReady(true));
    };
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
    return () => {
      window.removeEventListener("load", start);
      cancel();
    };
  }, [consent, ready]);

  if (!ready || (consent && !settings)) return null;
  return (
    <Suspense fallback={null}>
      <CookieConsent settings={settings} onClose={() => setSettings(false)} />
    </Suspense>
  );
}
